import { CheckIcon, ChevronDownIcon, SettingsIcon, UsersRoundIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Encabezado } from '@/components/Encabezado'
import { Pagina } from '@/components/Pagina'
import { Boton } from '@/components/ui/Boton'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
  MenuSeparador,
} from '@/components/ui/MenuDesplegable'
import { useSesionActiva } from '@/features/auth/RutaProtegida'
import { useContadores } from '@/features/publicacion/api'
import { cn } from '@/lib/cn'
import type { ProyectoPermitido } from '@/features/auth/useSesion'

interface EncabezadoProyectoProps {
  proyecto: ProyectoPermitido
  /** Acciones de RASTRO a la derecha (Nueva publicación, borradores). */
  acciones?: ReactNode
  /** Abre el modal de Equipo RASTRO desde el menú del avatar (§5.2). */
  alAbrirEquipo?: () => void
}

/**
 * C1 · encabezado de proyecto (§5.1 y §5.2). Arriba, la barra de siempre:
 * logo, Administración y avatar. Debajo, el titular de la página es
 * "Cliente · Proyecto", grande: desde la revisión del 21/09 el selector de
 * proyecto funciona como titular, para que se encuentre de un vistazo (antes
 * era texto chico junto al logo y se perdía). La vista, Grilla o
 * Calendario, ya la dice el conmutador de la fila de herramientas.
 *
 * Si la persona tiene más de un proyecto, el titular es un selector con "N
 * para revisar" en cada uno. Para RASTRO está siempre, aunque el proyecto sea
 * uno solo: es el camino de vuelta al resto de los clientes.
 */
export function EncabezadoProyecto({
  proyecto,
  acciones,
  alAbrirEquipo,
}: EncabezadoProyectoProps) {
  const { usuario, proyectos } = useSesionActiva()
  const navigate = useNavigate()
  const contadores = useContadores(proyectos.length > 1)
  const esRastro = usuario.tipo === 'rastro'
  // RASTRO siempre tiene a dónde ir: aunque el proyecto sea uno solo, desde
  // acá se llega a Administración, que es el mapa de clientes.
  const hayADondeIr = proyectos.length > 1 || esRastro

  const nombre = (
    <>
      <span className="text-tinta-suave">{proyecto.clienteNombre} · </span>
      {proyecto.nombre}
    </>
  )

  const titulo = (
    <h1
      data-titulo-vista
      className="flex min-w-0 items-center text-xl font-semibold tracking-tight sm:text-2xl"
    >
      {hayADondeIr ? (
        <MenuDesplegable>
          <MenuDisparador
            className="-ml-2 flex min-w-0 items-center gap-2 rounded-xl px-2 py-1 text-left hover:bg-tarjeta focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta"
            aria-label="Cambiar de proyecto"
          >
            <span className="truncate">{nombre}</span>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-tarjeta ring-1 ring-borde">
              <ChevronDownIcon className="size-4" aria-hidden />
            </span>
          </MenuDisparador>
          <MenuContenido align="start" className="max-w-[min(22rem,90vw)]">
            {proyectos.map((p) => {
              const n = contadores.data?.[p.id] ?? 0
              return (
                <MenuItem
                  key={p.id}
                  data-proyecto-opcion={p.id}
                  onSelect={() => {
                    void navigate(`/proyecto/${p.id}`)
                  }}
                >
                  <span className="flex w-full items-center gap-2">
                    <CheckIcon
                      className={cn(
                        'size-4 shrink-0',
                        p.id !== proyecto.id && 'invisible',
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {p.clienteNombre} · {p.nombre}
                    </span>
                    {n > 0 && (
                      <span className="shrink-0 rounded-full bg-revisar px-2 py-0.5 text-xs font-medium text-revisar-tinta">
                        {n} para revisar
                      </span>
                    )}
                  </span>
                </MenuItem>
              )
            })}
            {esRastro && (
              <>
                <MenuSeparador className="my-1 h-px bg-borde" />
                <MenuItem
                  onSelect={() => {
                    void navigate('/admin')
                  }}
                >
                  <span className="flex w-full items-center gap-2">
                    <SettingsIcon className="size-4 shrink-0" aria-hidden />
                    Ver todos los clientes
                  </span>
                </MenuItem>
              </>
            )}
          </MenuContenido>
        </MenuDesplegable>
      ) : (
        <span className="truncate">{nombre}</span>
      )}
    </h1>
  )

  return (
    <>
      <Encabezado
        usuario={usuario}
        acciones={
          <>
            {acciones}
            {esRastro && (
              <Boton
                asChild
                variante="sutil"
                tamano="chico"
                className="hidden sm:inline-flex"
              >
                <Link to="/admin">
                  <SettingsIcon className="size-4" aria-hidden />
                  Administración
                </Link>
              </Boton>
            )}
            {esRastro && (
              <Boton asChild variante="sutil" tamano="icono" className="sm:hidden">
                <Link to="/admin" aria-label="Administración">
                  <SettingsIcon className="size-5" aria-hidden />
                </Link>
              </Boton>
            )}
          </>
        }
        menuExtra={
          esRastro && alAbrirEquipo ? (
            <MenuItem onSelect={alAbrirEquipo}>
              <UsersRoundIcon className="size-4" aria-hidden />
              Equipo RASTRO
            </MenuItem>
          ) : undefined
        }
      />
      <Pagina className="pt-4 pb-1 sm:pt-6 lg:pt-3">{titulo}</Pagina>
    </>
  )
}
